<template>
  <div class="schema-field-list-grouped">
    <template v-if="segments.length > 0">
      <template v-for="(seg, sIdx) in segments">
        <!-- Fieldset segment -->
        <v-card
          v-if="seg.type === 'fieldset'"
          :key="'fs-' + seg.fieldset.key + '-' + sIdx"
          outlined
          class="mb-3"
        >
          <v-card-title class="py-2 px-4 subtitle-2 grey lighten-4 d-flex align-center">
            <v-icon small class="mr-2">mdi-tab</v-icon>
            {{ seg.fieldset.label || seg.fieldset.key }}
            <v-chip x-small outlined color="purple" class="ml-2">{{ seg.fieldset.key }}</v-chip>
            <v-chip v-if="seg.fieldset.depends_on" x-small outlined color="orange" class="ml-1">conditional</v-chip>
            <v-spacer />
            <v-btn icon x-small :disabled="sIdx === 0" class="mr-1" @click="moveSegment(sIdx, -1)">
              <v-icon x-small>mdi-arrow-up</v-icon>
            </v-btn>
            <v-btn icon x-small :disabled="sIdx === segments.length - 1" class="mr-2" @click="moveSegment(sIdx, 1)">
              <v-icon x-small>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn x-small text color="primary" @click="addFieldToSegment(sIdx)">
              <v-icon x-small left>mdi-plus</v-icon>
              Add
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-2">
            <v-expansion-panels multiple focusable>
              <schema-field-panel
                v-for="(entry, eIdx) in seg.entries"
                :key="entry.field._uid || entry.index"
                :field="entry.field"
                :sibling-fields="fields"
                :fieldset-options="fieldsetItems"
                :cms-types="cmsTypes"
                :fragment-types="fragmentTypes"
                :is-top-level="isTopLevel"
                :is-first="eIdx === 0"
                :is-last="eIdx === seg.entries.length - 1"
                @update="onFieldUpdate(entry.index, $event)"
                @move-up="moveFieldInSegment(sIdx, eIdx, -1)"
                @move-down="moveFieldInSegment(sIdx, eIdx, 1)"
                @duplicate="duplicateField(entry.index)"
                @remove="removeField(entry.index)"
                @edit-nested="$emit('edit-nested', entry.index)"
              />
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- Standalone field (no fieldset) -->
        <v-expansion-panels
          v-else
          :key="'field-' + seg.entries[0].index"
          multiple
          focusable
          class="mb-3"
        >
          <schema-field-panel
            v-for="entry in seg.entries"
            :key="entry.field._uid || entry.index"
            :field="entry.field"
            :sibling-fields="fields"
            :fieldset-options="fieldsetItems"
            :cms-types="cmsTypes"
            :fragment-types="fragmentTypes"
            :is-top-level="isTopLevel"
            :is-first="sIdx === 0"
            :is-last="sIdx === segments.length - 1"
            @update="onFieldUpdate(entry.index, $event)"
            @move-up="moveSegment(sIdx, -1)"
            @move-down="moveSegment(sIdx, 1)"
            @duplicate="duplicateField(entry.index)"
            @remove="removeField(entry.index)"
            @edit-nested="$emit('edit-nested', entry.index)"
          />
        </v-expansion-panels>
      </template>
    </template>

    <div v-else class="text-center grey--text py-6">
      No fields yet. Click "Add Field" to get started.
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import SchemaFieldPanel from './SchemaFieldPanel'
import { createDefaultField } from './schemaConstants'
import { makeUid, reassignUids } from './schemaUtils'

export default {
  components: {
    SchemaFieldPanel,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    fieldsets: {
      type: Array,
      default: () => [],
    },
    cmsTypes: {
      type: Array,
      default: () => [],
    },
    fragmentTypes: {
      type: Array,
      default: () => [],
    },
    isTopLevel: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    fieldsetItems() {
      return this.fieldsets.map(fs => ({
        text: fs.label || fs.key,
        value: fs.key,
      }))
    },
    fieldsetMap() {
      const map = {}
      for (const fs of this.fieldsets) {
        map[fs.key] = fs
      }
      return map
    },
    /**
     * Build segments from fields array preserving real order.
     * Consecutive fields with the same fieldset are grouped into one segment.
     * Fields without fieldset are each their own segment.
     */
    segments() {
      const result = []
      let current = null

      for (let i = 0; i < this.fields.length; i++) {
        const field = this.fields[i]
        const fsKey = field.fieldset && this.fieldsetMap[field.fieldset] ? field.fieldset : null

        if (fsKey) {
          if (current && current.type === 'fieldset' && current.fieldsetKey === fsKey) {
            current.entries.push({ field, index: i })
          } else {
            current = {
              type: 'fieldset',
              fieldsetKey: fsKey,
              fieldset: this.fieldsetMap[fsKey],
              entries: [{ field, index: i }],
            }
            result.push(current)
          }
        } else {
          current = {
            type: 'field',
            fieldsetKey: null,
            entries: [{ field, index: i }],
          }
          result.push(current)
          current = null
        }
      }

      return result
    },
  },
  methods: {
    onFieldUpdate(idx, updated) {
      const fields = cloneDeep(this.fields)
      fields[idx] = updated
      this.$emit('update', fields)
    },
    /**
     * Move an entire segment (fieldset group or standalone field) up or down
     */
    moveSegment(segIdx, direction) {
      const targetIdx = segIdx + direction
      if (targetIdx < 0 || targetIdx >= this.segments.length) {
        return
      }

      const fields = cloneDeep(this.fields)
      const seg = this.segments[segIdx]
      const target = this.segments[targetIdx]

      // Extract indices
      const segStart = seg.entries[0].index
      const segEnd = seg.entries[seg.entries.length - 1].index
      const segLen = segEnd - segStart + 1

      const targetStart = target.entries[0].index
      const targetEnd = target.entries[target.entries.length - 1].index
      const targetLen = targetEnd - targetStart + 1

      // Extract both chunks
      if (direction === -1) {
        // Move segment up: splice out segment, insert before target
        const segItems = fields.splice(segStart, segLen)
        fields.splice(targetStart, 0, ...segItems)
      } else {
        // Move segment down: splice out target, insert before segment
        const targetItems = fields.splice(targetStart, targetLen)
        fields.splice(segStart, 0, ...targetItems)
      }

      this.$emit('update', fields)
    },
    /**
     * Move field within a fieldset segment (swap with neighbor inside the group)
     */
    moveFieldInSegment(segIdx, entryIdx, direction) {
      const seg = this.segments[segIdx]
      const newEntryIdx = entryIdx + direction
      if (newEntryIdx < 0 || newEntryIdx >= seg.entries.length) {
        return
      }

      const fields = cloneDeep(this.fields)
      const idxA = seg.entries[entryIdx].index
      const idxB = seg.entries[newEntryIdx].index
      const temp = fields[idxA]
      fields[idxA] = fields[idxB]
      fields[idxB] = temp
      this.$emit('update', fields)
    },
    duplicateField(idx) {
      const fields = cloneDeep(this.fields)
      const dup = reassignUids(cloneDeep(fields[idx]))
      dup.key = dup.key + '_copy'
      fields.splice(idx + 1, 0, dup)
      this.$emit('update', fields)
    },
    removeField(idx) {
      const fields = cloneDeep(this.fields)
      fields.splice(idx, 1)
      this.$emit('update', fields)
    },
    addFieldToSegment(segIdx) {
      const seg = this.segments[segIdx]
      const fieldsetKey = seg.fieldsetKey || undefined
      const newField = createDefaultField(fieldsetKey)
      newField._uid = makeUid()
      const fields = cloneDeep(this.fields)
      const lastEntry = seg.entries[seg.entries.length - 1]
      fields.splice(lastEntry.index + 1, 0, newField)
      this.$emit('update', fields)
    },
  },
}
</script>
